import React from 'react';
import { colors, IMFormModal } from '@insurancemenu/imenu-components-lib';
import { AlertType, OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { observer } from 'mobx-react-lite';
import { useBiDashboardPageStore } from 'store/pages/tools/bi-groups-dashboard/pageStore';
import { MOVE_GROUP_MODAL_ID } from 'components/tools/bi-dashboard/constants';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import { ErrorOutline, InfoButton } from '@insurancemenu/imenu-components-lib/dist/icons';
import {
  CurrentAgencyBlockStyled,
  FormWrapperStyled,
  GroupNameStyled,
  AssigneesBlockStyled,
  AssigneesFieldStyled,
  AssigneesSpinnerStyled,
  CurrentAgencyStyled,
  CurrentAgencyLabelStyled,
  AlertInfoMessageStyled,
  IMSwissSelectStyled,
  AlertStyled,
  ContentStyled,
} from './styles';

interface AssigneesAlertProps {
  isTargetAgencySelected: boolean;
  isAssigneesSectionLoading: boolean;
  assigneeOptionsCount: number;
}

const AssigneesAlert: React.FC<AssigneesAlertProps> = ({
  isTargetAgencySelected,
  isAssigneesSectionLoading,
  assigneeOptionsCount,
}) => {
  const hasNoAvailableAssignees = isTargetAgencySelected && !isAssigneesSectionLoading && assigneeOptionsCount < 1;
  const type: AlertType = hasNoAvailableAssignees ? 'warning' : 'info';
  const Icon = hasNoAvailableAssignees ? ErrorOutline : InfoButton;

  const getMessage = (): string => {
    if (!isTargetAgencySelected) {
      return 'Select a target agency first to view available assignees';
    }

    if (hasNoAvailableAssignees) {
      return 'No assignees are available for the selected target agency';
    }

    return 'Only assignees from the selected target agency are available';
  };

  return (
    <AlertStyled
      type={type}
      content={[
        <AlertInfoMessageStyled key="assignees-alert-message">
          <Icon
            width="2.2rem"
            height="2.2rem"
            color={hasNoAvailableAssignees ? colors.yellow[600] : colors.blue[600]}
          />
          {getMessage()}
        </AlertInfoMessageStyled>,
      ]}
    />
  );
};

const MoveGroupModal: React.FC = observer(() => {
  const {
    groupInteractionDelegate: {
      moveModal,
      selectedGroup,
      moveGroupModalUiStore: {
        searchAgencyDelegate,
        form,
        isAssigneesSectionLoading,
        assigneeOptions,
        resetModalData,
        currentAgencyName,
      },
    },
  } = useBiDashboardPageStore();

  const agencyOptions: OptionType[] = searchAgencyDelegate.allSponsorAgencies.map((sponsorAgency) => ({
    label: sponsorAgency.name,
    value: sponsorAgency.id,
  }));

  const isTargetAgencySelected = Boolean(form.fields.agencyId.value[0]);

  const onClose = (): void => {
    resetModalData();
    moveModal.close();
  };

  return (
    <IMFormModal
      show={moveModal.isVisible}
      title="Move Group"
      subtitle={
        <>
          Move <GroupNameStyled>{selectedGroup.name}</GroupNameStyled> to another agency
          <br />
          and choose its new assignees
        </>
      }
      closeButtonText="Cancel"
      submitButtonText="Move group"
      onClose={onClose}
      modalType={ModalType.NO_ICON}
      formController={form}
      formId={MOVE_GROUP_MODAL_ID}
      formContent={
        <ContentStyled>
          <CurrentAgencyBlockStyled>
            <CurrentAgencyLabelStyled>Current agency</CurrentAgencyLabelStyled>
            <CurrentAgencyStyled>{currentAgencyName}</CurrentAgencyStyled>
          </CurrentAgencyBlockStyled>
          <FormWrapperStyled>
            <IMSwissSelectStyled
              name="agencyId"
              label="Target agency"
              options={agencyOptions}
              placeholder="Select agency"
              size="full"
              withSearch
              searchPlaceholder="Agency name"
            />
            <AssigneesBlockStyled>
              <AssigneesFieldStyled>
                <IMSwissSelectStyled
                  name="assigneeIds"
                  label="Assignees"
                  options={assigneeOptions}
                  placeholder="Select assignees"
                  size="full"
                  multiselect
                  withSearch
                  disabled={!isTargetAgencySelected || assigneeOptions.length < 1 || isAssigneesSectionLoading}
                  searchPlaceholder="Assignee name"
                />
                <AssigneesSpinnerStyled display={isAssigneesSectionLoading} overlay="none" size="2rem" />
              </AssigneesFieldStyled>
              <AssigneesAlert
                isTargetAgencySelected={isTargetAgencySelected}
                isAssigneesSectionLoading={isAssigneesSectionLoading}
                assigneeOptionsCount={assigneeOptions.length}
              />
            </AssigneesBlockStyled>
          </FormWrapperStyled>
        </ContentStyled>
      }
    />
  );
});

export default MoveGroupModal;
